export interface Scene {
  mount(root: HTMLElement): Promise<void>;
  unmount(): void;
}

export type SceneTransitionDirection = "forward" | "back";

export interface SceneTransitionOptions {
  direction: SceneTransitionDirection;
}

export class SceneManager {
  private current: Scene | null = null;
  private currentLayer: HTMLElement | null = null;
  private readonly transitionAccent: HTMLElement;
  private transitioning = false;

  constructor(private readonly root: HTMLElement) {
    this.transitionAccent = document.createElement("div");
    this.transitionAccent.className = "scene-transition-accent";
    this.transitionAccent.setAttribute("aria-hidden", "true");
    this.root.append(this.transitionAccent);
  }

  async change(next: Scene): Promise<void> {
    this.current?.unmount();
    this.currentLayer?.remove();
    const layer = this.createLayer();
    this.root.insertBefore(layer, this.transitionAccent);
    this.current = next;
    this.currentLayer = layer;
    await next.mount(layer);
  }

  async transitionTo(next: Scene, options: SceneTransitionOptions): Promise<boolean> {
    if (this.transitioning || !this.current || !this.currentLayer) return false;

    this.transitioning = true;
    const outgoing = this.currentLayer;
    const incoming = this.createLayer();
    const direction = options.direction;
    outgoing.classList.add("scene-layer-transition", `scene-layer-outgoing-${direction}`);
    incoming.classList.add("scene-layer-transition", `scene-layer-incoming-${direction}`);
    this.root.insertBefore(incoming, this.transitionAccent);

    try {
      await next.mount(incoming);
      void incoming.offsetWidth;
      this.transitionAccent.classList.add("is-transitioning", `is-${direction}`);
      void this.transitionAccent.offsetWidth;
      this.requestFrame(() => {
        outgoing.classList.add("scene-layer-transition-active");
        incoming.classList.add("scene-layer-transition-active");
        this.transitionAccent.classList.add("is-active");
      });
      await this.waitForTransition();
    } catch (error) {
      next.unmount();
      incoming.remove();
      outgoing.className = "scene-layer";
      this.transitionAccent.className = "scene-transition-accent";
      this.transitioning = false;
      throw error;
    }

    this.current?.unmount();
    outgoing.remove();
    incoming.className = "scene-layer";
    this.current = next;
    this.currentLayer = incoming;
    this.transitionAccent.className = "scene-transition-accent";
    this.transitioning = false;
    return true;
  }

  private createLayer(): HTMLElement {
    const layer = document.createElement("div");
    layer.className = "scene-layer";
    return layer;
  }

  private requestFrame(callback: () => void): void {
    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => callback());
      return;
    }
    window.setTimeout(callback, 0);
  }

  private waitForTransition(): Promise<void> {
    const reducedMotion = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    return new Promise((resolve) => window.setTimeout(resolve, reducedMotion ? 60 : 220));
  }

  getRoot(): HTMLElement {
    return this.root;
  }
}
